"use client";

import { cn } from "@/lib/utils";
import { Info } from "@phosphor-icons/react";
import { useId, useState } from "react";

interface PageHeaderSectionProps {
  title: string;
  subtitle?: string | null;
  showTitle?: boolean;
  showDescription?: boolean;
  /** Merged onto the root `<header>` (after defaults). */
  className?: string;
  /** Merged onto the `<h1>` (after defaults). */
  titleClassName?: string;
  /** Merged onto the description `<p>` (after defaults). */
  descriptionClassName?: string;
  /** Merged onto the info `<button>` (after defaults). */
  infoButtonClassName?: string;
}

export function PageHeaderSection({
  title,
  subtitle,
  showTitle = true,
  showDescription = false,
  className,
  titleClassName,
  descriptionClassName,
  infoButtonClassName,
}: PageHeaderSectionProps) {
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const descriptionRegionId = useId();

  const hasDescription = showDescription && Boolean(subtitle);

  // Avoid leaving an empty <header className="mb-12"> when title/description are off
  // (common on branded set pages) — that margin alone opens a gap above the games.
  if (!showTitle && !hasDescription) {
    return null;
  }

  return (
    <header className={cn("text-left mb-12", className)}>
      <div className="flex items-center gap-2">
        {showTitle && (
          <h1 className={cn("text-4xl md:text-5xl font-bold text-white mb-4", titleClassName)}>
            {title}
          </h1>
        )}
        {hasDescription && (
          <button
            type="button"
            onClick={() => setIsDescriptionExpanded((prev) => !prev)}
            className={cn(
              "mb-4 hover:opacity-80 transition-opacity cursor-pointer",
              infoButtonClassName
            )}
            aria-label={
              isDescriptionExpanded ? "Collapse page description" : "Expand page description"
            }
            aria-expanded={isDescriptionExpanded}
            aria-controls={descriptionRegionId}
          >
            <Info className="h-5 w-5 text-white shrink-0" weight="fill" />
          </button>
        )}
      </div>

      {hasDescription && (
        <section
          id={descriptionRegionId}
          aria-label="Page description"
          aria-hidden={!isDescriptionExpanded}
          className={`grid transition-[grid-template-rows,opacity] duration-300 ease-out ${
            isDescriptionExpanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
          }`}
        >
          <div className="overflow-hidden">
            <p
              className={cn(
                "text-xl text-white/80 transition-transform duration-300 ease-out",
                isDescriptionExpanded ? "translate-y-0" : "-translate-y-2",
                descriptionClassName
              )}
            >
              {subtitle}
            </p>
          </div>
        </section>
      )}
    </header>
  );
}
